// A horizontal scrollbar pinned to the bottom of the viewport, mirroring a
// wide scroll container further down the page.
//
// The problem it solves: a long table's own horizontal scrollbar sits at the
// *bottom of the table*, so with a full month of rows you have to scroll all
// the way down before you can side-scroll at all. This renders a proxy
// scrollbar over the bottom of the screen and keeps its scrollLeft in sync
// with the real container both ways.
//
// It hides itself when there's nothing to scroll horizontally, and also as
// soon as the container's own scrollbar is on screen — so you never see two.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { colors } from '../styles/theme';

const BAR_HEIGHT = 14;

interface Props {
  /** The element with overflow-x: auto whose scroll position we mirror. */
  targetRef: React.RefObject<HTMLElement | null>;
}

interface Metrics {
  left: number;
  width: number;
  scrollWidth: number;
}

export default function FloatingHScrollbar({ targetRef }: Props) {
  const barRef = useRef<HTMLDivElement>(null);
  const [metrics, setMetrics] = useState<Metrics | null>(null);

  // Guards the two scroll handlers against echoing each other into a loop.
  const syncing = useRef(false);

  const measure = useCallback(() => {
    const el = targetRef.current;
    if (!el) { setMetrics(null); return; }

    const rect = el.getBoundingClientRect();
    const overflows = el.scrollWidth - el.clientWidth > 1;
    // The container's own scrollbar lives at rect.bottom. Once that's visible
    // (or the container is scrolled past entirely) the proxy is redundant.
    const ownBarBelowFold = rect.bottom > window.innerHeight;
    const onScreen = rect.top < window.innerHeight && rect.bottom > 0;

    if (!overflows || !ownBarBelowFold || !onScreen) { setMetrics(null); return; }

    setMetrics(prev => {
      const next = { left: rect.left, width: el.clientWidth, scrollWidth: el.scrollWidth };
      return prev && prev.left === next.left && prev.width === next.width && prev.scrollWidth === next.scrollWidth
        ? prev
        : next;
    });
  }, [targetRef]);

  // Re-measure on anything that can move the container or change its width:
  // page scroll, viewport resize, column drag-resize, subledger columns being
  // added/removed, rows loading in.
  useEffect(() => {
    let frame = 0;
    const schedule = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => { frame = 0; measure(); });
    };

    schedule();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);

    const ro = new ResizeObserver(schedule);
    const el = targetRef.current;
    if (el) {
      ro.observe(el);
      // The table itself is what actually grows; observing only the wrapper
      // misses column resizes, which change scrollWidth but not clientWidth.
      const table = el.querySelector('table');
      if (table) ro.observe(table);
    }

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      ro.disconnect();
    };
  }, [measure, targetRef]);

  // Container -> proxy
  useEffect(() => {
    const el = targetRef.current;
    if (!el || !metrics) return;
    const onScroll = () => {
      if (syncing.current) { syncing.current = false; return; }
      if (barRef.current) {
        syncing.current = true;
        barRef.current.scrollLeft = el.scrollLeft;
      }
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [metrics, targetRef]);

  // Adopt the container's current offset whenever the proxy (re)appears.
  useEffect(() => {
    if (metrics && barRef.current && targetRef.current) {
      barRef.current.scrollLeft = targetRef.current.scrollLeft;
    }
  }, [metrics, targetRef]);

  if (!metrics) return null;

  // Proxy -> container
  const handleBarScroll = () => {
    const el = targetRef.current;
    if (!el || !barRef.current) return;
    if (syncing.current) { syncing.current = false; return; }
    syncing.current = true;
    el.scrollLeft = barRef.current.scrollLeft;
  };

  return (
    <div
      ref={barRef}
      onScroll={handleBarScroll}
      style={{
        position: 'fixed',
        bottom: 0,
        left: metrics.left,
        width: metrics.width,
        height: BAR_HEIGHT,
        overflowX: 'auto',
        overflowY: 'hidden',
        zIndex: 900,
        background: colors.surface,
        borderTop: `1px solid ${colors.border}`,
        boxShadow: '0 -2px 6px rgba(0,0,0,0.08)',
      }}
    >
      <div style={{ width: metrics.scrollWidth, height: 1 }} />
    </div>
  );
}
